import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Divider,
  Box,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';

/**
 * MuiCard - A styled Card component following UX principles
 *
 * Jakob's Law: Follows familiar card patterns users expect
 * Law of Proximity: Groups related content in sections
 * Von Restorff Effect: Highlights important actions with primary color
 */

const StyledCard = styled(Card)(({ theme, variant }) => ({
  borderRadius: 8,
  boxShadow: variant === 'elevated'
    ? '0 4px 24px rgba(0, 0, 0, 0.08)'
    : '0 1px 3px rgba(0, 0, 0, 0.05)',
  border: variant === 'outlined' ? `1px solid ${theme.palette.divider}` : 'none',
  transition: 'box-shadow 0.2s ease-in-out',
  '&:hover': {
    boxShadow: variant === 'elevated'
      ? '0 8px 32px rgba(0, 0, 0, 0.12)'
      : '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
}));

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  '& .MuiCardHeader-title': {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& .MuiCardHeader-subheader': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'noPadding',
})(({ theme, noPadding }) => ({
  padding: noPadding ? 0 : theme.spacing(2, 3),
  '&:last-child': {
    paddingBottom: noPadding ? 0 : theme.spacing(2),
  },
}));

const StyledCardActions = styled(CardActions)(({ theme, position }) => ({
  padding: theme.spacing(1.5, 3),
  justifyContent: position === 'right' ? 'flex-end' : position === 'center' ? 'center' : 'flex-start',
  gap: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const MuiCard = ({
  title,
  subtitle,
  avatar,
  action,
  children,
  actions,
  actionsPosition = 'right',
  variant = 'elevated',
  collapsible = false,
  defaultExpanded = true,
  showDivider = false,
  showMoreMenu = false,
  onMoreClick,
  noPadding = false,
  headerProps = {},
  contentProps = {},
  actionsProps = {},
  sx = {},
  ...props
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const renderHeaderAction = () => {
    const actionElements = [];

    if (action) {
      actionElements.push(
        <React.Fragment key="custom-action">{action}</React.Fragment>
      );
    }

    if (showMoreMenu) {
      actionElements.push(
        <IconButton
          key="more-menu"
          aria-label="more options"
          onClick={onMoreClick}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      );
    }

    if (collapsible) {
      actionElements.push(
        <ExpandMore
          key="expand"
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          size="small"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      );
    }

    return actionElements.length > 0 ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {actionElements}
      </Box>
    ) : null;
  };

  const content = (
    <>
      <StyledCardContent noPadding={noPadding} {...contentProps}>
        {children}
      </StyledCardContent>
      {actions && (
        <StyledCardActions position={actionsPosition} {...actionsProps}>
          {actions}
        </StyledCardActions>
      )}
    </>
  );

  return (
    <StyledCard variant={variant} sx={sx} {...props}>
      {(title || subtitle || avatar || action || collapsible || showMoreMenu) && (
        <>
          <StyledCardHeader
            avatar={avatar}
            title={title}
            subheader={subtitle}
            action={renderHeaderAction()}
            {...headerProps}
          />
          {showDivider && <Divider />}
        </>
      )}
      {collapsible ? (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {content}
        </Collapse>
      ) : (
        content
      )}
    </StyledCard>
  );
};

MuiCard.propTypes = {
  /** Card title */
  title: PropTypes.node,
  /** Card subtitle */
  subtitle: PropTypes.node,
  /** Avatar element for the header */
  avatar: PropTypes.node,
  /** Custom action element for the header */
  action: PropTypes.node,
  /** Card content */
  children: PropTypes.node,
  /** Action buttons for the footer */
  actions: PropTypes.node,
  /** Position of action buttons */
  actionsPosition: PropTypes.oneOf(['left', 'center', 'right']),
  /** Card style variant */
  variant: PropTypes.oneOf(['elevated', 'outlined', 'flat']),
  /** Enable collapsible content */
  collapsible: PropTypes.bool,
  /** Default expanded state for collapsible cards */
  defaultExpanded: PropTypes.bool,
  /** Show divider between header and content */
  showDivider: PropTypes.bool,
  /** Show more menu icon */
  showMoreMenu: PropTypes.bool,
  /** Callback for more menu click */
  onMoreClick: PropTypes.func,
  /** Remove padding from content */
  noPadding: PropTypes.bool,
  /** Additional props for CardHeader */
  headerProps: PropTypes.object,
  /** Additional props for CardContent */
  contentProps: PropTypes.object,
  /** Additional props for CardActions */
  actionsProps: PropTypes.object,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiCard;
